// ============================================
// SammyGas - Contexto del Carrito
// ============================================

import React, { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { STORAGE_KEYS, CART_LIMITS, SHIPPING_CONFIG } from '../utils/constants';

// Crear el contexto
export const CartContext = createContext(null);

/**
 * Recupera el carrito del localStorage de forma segura
 * @returns {array} Items del carrito
 */
const getStoredCart = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CART);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error al leer carrito del localStorage:', error);
    return [];
  }
};

/**
 * Recupera la zona seleccionada del localStorage
 * @returns {object|null} Zona seleccionada
 */
const getStoredZone = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_ZONE);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error al leer zona del localStorage:', error);
    return null;
  }
};

/**
 * Guarda el carrito en localStorage
 * @param {array} items - Items del carrito
 */
const saveCartToStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(items));
  } catch (error) {
    console.error('Error al guardar carrito:', error);
  }
};

/**
 * Guarda la zona en localStorage
 * @param {object} zone - Zona seleccionada
 */
const saveZoneToStorage = (zone) => {
  try {
    if (zone) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_ZONE, JSON.stringify(zone));
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_ZONE);
    }
  } catch (error) {
    console.error('Error al guardar zona:', error);
  }
};

/**
 * Provider del carrito que envuelve la aplicación
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export const CartProvider = ({ children }) => {
  // Estados
  const [items, setItems] = useState([]);
  const [selectedZone, setSelectedZoneState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ============================================
  // INICIALIZACIÓN - Cargar desde localStorage
  // ============================================
  useEffect(() => {
    const storedCart = getStoredCart();
    const storedZone = getStoredZone();
    
    setItems(storedCart);
    setSelectedZoneState(storedZone);
    setIsLoading(false);
  }, []);

  // ============================================
  // PERSISTENCIA - Guardar en localStorage
  // ============================================
  useEffect(() => {
    if (!isLoading) {
      saveCartToStorage(items);
    }
  }, [items, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      saveZoneToStorage(selectedZone);
    }
  }, [selectedZone, isLoading]);

  // ============================================
  // AÑADIR ITEM AL CARRITO
  // ============================================
  const addItem = useCallback((product, quantity = 1) => {
    // Validar stock disponible
    if (product.stock !== undefined && product.stock !== null) {
      const existingItem = items.find(item => item.product.id === product.id);
      const currentQuantity = existingItem ? existingItem.quantity : 0;
      
      if (currentQuantity + quantity > product.stock) {
        return {
          success: false,
          error: `Solo hay ${product.stock} unidades disponibles`,
          available: product.stock - currentQuantity,
        };
      }
    }

    // Validar límite máximo por producto
    const existingItem = items.find(item => item.product.id === product.id);
    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      if (newQuantity > CART_LIMITS.MAX_QUANTITY_PER_ITEM) {
        return {
          success: false,
          error: `Máximo ${CART_LIMITS.MAX_QUANTITY_PER_ITEM} unidades por producto`,
        };
      }
    }

    setItems(currentItems => {
      const existingIndex = currentItems.findIndex(
        item => item.product.id === product.id
      );

      if (existingIndex >= 0) {
        // Producto ya existe, incrementar cantidad
        const updatedItems = [...currentItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + quantity,
        };
        return updatedItems;
      } else {
        // Nuevo producto
        return [...currentItems, { product, quantity }];
      }
    });

    return { success: true };
  }, [items]);

  // ============================================
  // ELIMINAR ITEM DEL CARRITO
  // ============================================
  const removeItem = useCallback((productId) => {
    setItems(currentItems => 
      currentItems.filter(item => item.product.id !== productId)
    );
  }, []);

  // ============================================
  // ACTUALIZAR CANTIDAD
  // ============================================
  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return { success: true };
    }

    // Validar límite máximo
    if (quantity > CART_LIMITS.MAX_QUANTITY_PER_ITEM) {
      return {
        success: false,
        error: `Máximo ${CART_LIMITS.MAX_QUANTITY_PER_ITEM} unidades por producto`,
      };
    }

    setItems(currentItems => {
      return currentItems.map(item => {
        if (item.product.id === productId) {
          // Validar stock
          if (item.product.stock !== undefined && item.product.stock !== null) {
            if (quantity > item.product.stock) {
              return item; // No actualizar si excede stock
            }
          }
          return { ...item, quantity };
        }
        return item;
      });
    });

    return { success: true };
  }, [removeItem]);

  // ============================================
  // LIMPIAR CARRITO
  // ============================================
  const clearCart = useCallback(() => {
    setItems([]);
    // No limpiar la zona para mantenerla para futuros pedidos
  }, []);

  // ============================================
  // SELECCIONAR ZONA
  // ============================================
  const setSelectedZone = useCallback((zone) => {
    setSelectedZoneState(zone);
  }, []);

  // ============================================
  // CÁLCULOS MEMOIZADOS
  // ============================================
  
  // Total de items en el carrito
  const totalItems = useMemo(() => {
    return items.reduce((total, item) => total + item.quantity, 0);
  }, [items]);

  // Subtotal (suma de precios * cantidades)
  const subtotal = useMemo(() => {
    return items.reduce((total, item) => {
      // Usar current_price del backend (precio final calculado)
      const price = item.product.current_price || item.product.price || 0;
      return total + (price * item.quantity);
    }, 0);
  }, [items]);

  // Costo de envío
  const shippingCost = useMemo(() => {
    // Si no hay items, no hay envío
    if (items.length === 0) return 0;
    
    // Envío gratis si supera el umbral
    if (subtotal >= SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD) {
      return 0;
    }
    
    // Costo según zona seleccionada
    return selectedZone?.cost || SHIPPING_CONFIG.DEFAULT_SHIPPING_COST;
  }, [items, subtotal, selectedZone]);

  // Total con envío
  const total = useMemo(() => {
    return subtotal + shippingCost;
  }, [subtotal, shippingCost]);

  // Si aplica envío gratis
  const hasFreeShipping = useMemo(() => {
    return subtotal >= SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD;
  }, [subtotal]);

  // Cuánto falta para envío gratis
  const amountForFreeShipping = useMemo(() => {
    if (hasFreeShipping) return 0;
    return SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD - subtotal;
  }, [subtotal, hasFreeShipping]);

  // Verificar si el carrito cumple el monto mínimo
  const meetsMinimumOrder = useMemo(() => {
    return subtotal >= CART_LIMITS.MIN_ORDER_AMOUNT;
  }, [subtotal]);

  // ============================================
  // OBTENER ITEM ESPECÍFICO
  // ============================================
  const getItem = useCallback((productId) => {
    return items.find(item => item.product.id === productId) || null;
  }, [items]);

  // ============================================
  // VALIDAR STOCK DE TODOS LOS ITEMS
  // ============================================
  const validateStock = useCallback(() => {
    const errors = [];
    
    items.forEach(item => {
      if (item.product.stock !== undefined && item.product.stock !== null) {
        if (item.quantity > item.product.stock) {
          errors.push({
            productId: item.product.id,
            productName: item.product.name,
            requested: item.quantity,
            available: item.product.stock,
          });
        }
      }
    });
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }, [items]);

  // ============================================
  // SINCRONIZAR CON BACKEND (opcional)
  // ============================================
  const syncWithServer = useCallback(async () => {
    // Aquí se podría implementar sincronización con backend
    // para carritos persistentes en base de datos
  }, []);

  // ============================================
  // VALOR DEL CONTEXTO
  // ============================================
  const value = {
    // Estados
    items,
    selectedZone,
    isLoading,
    
    // Funciones
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setSelectedZone,
    getItem,
    validateStock,
    syncWithServer,
    
    // Cálculos
    totalItems,
    subtotal,
    shippingCost,
    total,
    hasFreeShipping,
    amountForFreeShipping,
    meetsMinimumOrder,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Hook personalizado
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};

export default CartContext;
